/**
 * BDL Catálogo — grid de produtos com sort, "mostrar por página" e "carregar mais",
 * tudo sem reload de página.
 *
 * Usa a Section Rendering API do Shopify (?section_id=...), que é pública e estável —
 * não depende dos métodos internos do paginated-list.js/facets.js do Horizon (esses
 * mudam de versão pra versão, ver docs/plano-2-melhorias.md sobre isso).
 *
 * Paginação base do Shopify fica fixa em 8 produtos por página (default do
 * seletor "Mostrar por página"). No desktop o seletor é um multiplicador de
 * quantas páginas de 8 buscar de uma vez — 16 = 2 páginas, 48 = 6 páginas.
 * Não dá pra pedir 48 numa tacada: o {% paginate %} do Liquid é fixo e o Liquid
 * não lê parâmetro da URL, então o tamanho do lote só existe aqui no JS.
 */
class BdlCatalogGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-bdl-catalog-grid]');
    this.loadMoreBtn = this.querySelector('[data-bdl-catalog-load-more]');
    this.filterToggle = this.querySelector('[data-bdl-catalog-filter-toggle]');
    this.sidebar = this.querySelector('[data-bdl-catalog-sidebar]');
    this.sortSelect = this.querySelector('[data-bdl-catalog-sort]');
    this.perPageSelect = this.querySelector('[data-bdl-catalog-per-page]');

    this.sectionId = this.dataset.sectionId;
    this.baseUrl = this.dataset.baseUrl;
    this.currentPage = Number(this.grid?.dataset.currentPage || 1);
    this.lastPage = Number(this.grid?.dataset.lastPage || 1);
    this.isLoading = false;

    this.filterToggle?.addEventListener('click', () => this.toggleSidebar());
    this.loadMoreBtn?.addEventListener('click', () => this.loadMore());
    this.sortSelect?.addEventListener('change', () => this.reload());
    this.perPageSelect?.addEventListener('change', () => this.reload());

    this.updateLoadMoreVisibility();
    this.observePriceFilterLabel();
  }

  /**
   * O rótulo "Price" do filtro de faixa de preço vem pronto do Search &
   * Discovery nativo (filter.label, dado do Admin) — não existe locale do
   * tema pra traduzir e price-filter.liquid/list-filter.liquid não estão na
   * lista de arquivos liberados pra edição direta. Faz um relabel por JS,
   * igual ao "Comprar Agora" do card de produto.
   * O facets.js nativo re-renderiza essa sidebar sozinho quando um filtro
   * muda (fora do nosso controle/Section Rendering API), então um relabel
   * único no load não basta: observa mudanças no DOM da sidebar pra
   * reaplicar sempre que "Price" reaparecer.
   */
  observePriceFilterLabel() {
    if (!this.sidebar) return;

    const relabel = () => {
      this.sidebar.querySelectorAll('.facets__label').forEach((el) => {
        if (el.textContent.trim() === 'Price') el.textContent = 'Preço';
      });
    };

    relabel();
    new MutationObserver(relabel).observe(this.sidebar, { childList: true, subtree: true, characterData: true });
  }

  toggleSidebar() {
    if (!this.sidebar || !this.filterToggle) return;
    const isOpen = this.sidebar.hasAttribute('data-open');

    if (isOpen) {
      this.sidebar.removeAttribute('data-open');
      this.filterToggle.classList.remove('is-active');
    } else {
      this.sidebar.setAttribute('data-open', '');
      this.filterToggle.classList.add('is-active');
    }

    const label = this.filterToggle.querySelector('[data-bdl-catalog-filter-label]');
    if (label) {
      label.textContent = isOpen
        ? this.filterToggle.dataset.labelClosed
        : this.filterToggle.dataset.labelOpen;
    }
  }

  /** @param {number} page */
  buildUrl(page) {
    const url = new URL(this.baseUrl, window.location.origin);
    const current = new URL(window.location.href);

    // Preserva filtros nativos já aplicados (filter.*) — ex.: sidebar de filtros nativa.
    for (const [key, value] of current.searchParams.entries()) {
      if (key.startsWith('filter.')) url.searchParams.append(key, value);
    }

    // Algumas opções de "Ordenar por" (Promoções, Sem açúcar) não são sort_by nativo —
    // são um filtro de tag disfarçado, codificado como "filter.p.tag=Valor" no value do <option>.
    const sortValue = this.sortSelect?.value;
    if (sortValue?.startsWith('filter.')) {
      const [filterKey, filterValue] = sortValue.split('=');
      if (filterKey && filterValue) url.searchParams.set(filterKey, decodeURIComponent(filterValue));
    } else if (sortValue) {
      url.searchParams.set('sort_by', sortValue);
    }

    if (page > 1) url.searchParams.set('page', String(page));

    return url;
  }

  /** @param {number} page */
  async fetchPage(page) {
    const url = this.buildUrl(page);
    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    const response = await fetch(fetchUrl.toString());
    if (!response.ok) return null;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = doc.querySelector('[data-bdl-catalog-grid]');
    if (!newGrid) return null;

    return { newGrid, url };
  }

  updateLoadMoreVisibility() {
    if (!this.loadMoreBtn) return;
    this.loadMoreBtn.hidden = this.currentPage >= this.lastPage;
  }

  /** Quantas páginas de 8 formam um lote, conforme "Mostrar por página". */
  batchSize() {
    return Math.max(1, Number(this.perPageSelect?.value) || 1);
  }

  /**
   * Busca um lote de `count` páginas a partir de `startPage` e devolve os cards
   * na ordem. A primeira página vai sozinha porque é ela que informa o total de
   * páginas — só depois disso dá pra pedir o resto em paralelo sem chutar
   * páginas inexistentes (página fora do range devolveria conteúdo repetido).
   * @param {number} startPage
   * @param {number} count
   */
  async loadBatch(startPage, count) {
    const first = await this.fetchPage(startPage);
    if (!first) return null;

    const cards = [...first.newGrid.querySelectorAll(':scope > [data-bdl-catalog-card]')];
    const lastPage = Number(first.newGrid.dataset.lastPage || this.lastPage);
    let endPage = startPage;

    const pending = [];
    for (let page = startPage + 1; page < startPage + count && page <= lastPage; page += 1) {
      pending.push(page);
    }

    if (pending.length > 0) {
      const results = await Promise.all(pending.map((page) => this.fetchPage(page)));
      for (let i = 0; i < results.length; i += 1) {
        // Para no primeiro furo: seguir adiante pularia produtos silenciosamente.
        if (!results[i]) break;
        cards.push(...results[i].newGrid.querySelectorAll(':scope > [data-bdl-catalog-card]'));
        endPage = pending[i];
      }
    }

    return { cards, lastPage, endPage, url: first.url };
  }

  async loadMore() {
    if (this.isLoading || this.currentPage >= this.lastPage) return;
    this.isLoading = true;
    this.loadMoreBtn.disabled = true;

    const batch = await this.loadBatch(this.currentPage + 1, this.batchSize());
    if (batch) {
      batch.cards.forEach((card) => this.grid.appendChild(card));
      this.currentPage = batch.endPage;
      this.lastPage = batch.lastPage;
      this.grid.dataset.currentPage = String(this.currentPage);
      history.replaceState({}, '', this.buildUrl(this.currentPage).toString());
      this.updateLoadMoreVisibility();
    }

    this.loadMoreBtn.disabled = false;
    this.isLoading = false;
  }

  /**
   * Sort ou "mostrar por página" mudou — refaz do zero, já trazendo o lote
   * inteiro (escolher 48 mostra 48 na hora, não 8).
   */
  async reload() {
    if (this.isLoading) return;
    this.isLoading = true;

    const batch = await this.loadBatch(1, this.batchSize());
    if (batch) {
      this.grid.replaceChildren(...batch.cards);
      this.currentPage = batch.endPage;
      this.lastPage = batch.lastPage;
      this.grid.dataset.currentPage = String(this.currentPage);
      history.pushState({}, '', batch.url.toString());
      this.updateLoadMoreVisibility();
    }

    this.isLoading = false;
  }
}

if (!customElements.get('bdl-catalog-grid')) {
  customElements.define('bdl-catalog-grid', BdlCatalogGrid);
}
